import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Wand2, Sparkles, Image as ImageIcon } from 'lucide-react';
import MediaPackGenerator from './MediaPackGenerator';
import SuccessVisualGenerator from './SuccessVisualGenerator';
import PackCard from './PackCard';

/**
 * OpportunityMediaLauncher — injected in OpportunityDetail's AI Intelligence tab
 * between the Vision Proposal and AI Outreach Email cards. Lets a rep turn the
 * Style Opportunity into public-safe marketing content without leaving the CRM.
 *
 * Both entry points reuse the exact same Media Studio components (no duplicate
 * workflow) — pre-seeded with this opportunity so its context is already loaded.
 */
export default function OpportunityMediaLauncher({ opportunityId, opportunity }) {
  const [open, setOpen] = useState(false);
  const [svOpen, setSvOpen] = useState(false);
  const [pack, setPack] = useState(null);
  const [usage, setUsage] = useState(null);

  const oppLabel = opportunity?.company_name || opportunity?.property_type || 'this opportunity';
  const defaultTopic = opportunity?.core_problem || opportunity?.property_type || '';

  return (
    <>
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="opportunity-media-launcher">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-[#B89B5E]/20 to-transparent">
                <Wand2 className="h-5 w-5 text-[#B89B5E]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-[#1A1A1A]">Media Studio</h3>
                  <Badge variant="outline" className="text-[10px] border-[#B89B5E]/40 text-[#B89B5E]">Public-safe</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Turn this opportunity into social posts, a video script, or a success visual.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setSvOpen(true)} className="border-[#B89B5E]/50 text-[#1A1A1A]" data-testid="launch-success-visual-btn">
                <ImageIcon className="h-4 w-4 mr-2" />
                Create Success Visual
              </Button>
              <Button onClick={() => setOpen(true)} className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white" data-testid="launch-media-studio-btn">
                <Sparkles className="h-4 w-4 mr-2" />
                Create Content
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Media Pack from this Opportunity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <MediaPackGenerator
              opportunityId={opportunityId}
              defaultTopic={defaultTopic}
              onGenerated={(p) => setPack(p)}
              onUsage={setUsage}
              compact
            />
            {pack && <PackCard pack={pack} usage={usage} onDeleted={() => setPack(null)} />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={svOpen} onOpenChange={setSvOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create a Success Visual from this Opportunity</DialogTitle>
          </DialogHeader>
          <SuccessVisualGenerator
            opportunityId={opportunityId}
            opportunityLabel={oppLabel}
            defaultTopic={defaultTopic}
            compact
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
